import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  Button,
  makeStyles,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import logout from "utils/logout";
import PageLoading from "components/PageLoading";
import UserFormModal, { UserSaveProps } from "components/UserFormModal";
import { AuthenticatedUser } from "@dashboard-v3/api";
import { updateUser } from "pages/Users/utils";
import { getErrorCode } from "utils/apiRequestWithErrorCode";

const ToolbarBtn = styled(Button)`
  justify-self: end;
  max-width: 250px;

  .MuiButton-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

interface HeaderMenuProps {
  user: AuthenticatedUser;
  onUserChange: React.Dispatch<React.SetStateAction<AuthenticatedUser>>;
}

export default function HeaderMenu({ user, onUserChange }: HeaderMenuProps) {
  const { t } = useTranslation(["components", "users"]);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [userFormIsOpen, setUserFormIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const { name, email } = user;
  const classes = useStyles();

  async function handleSave(changes: UserSaveProps) {
    const { id, ...userData } = changes;
    setIsLoading(true);
    try {
      const updatedUser = await updateUser(id, userData);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      onUserChange(() => {
        const newState = { ...updatedUser, name: userData.name };
        return newState;
      });
    } catch (error) {
      const code = getErrorCode(error);
      const errorMessages = [
        `users:error.patchUser.${code}`,
        "users:error.patchUser.SYSTEM_FAILURE",
      ];

      enqueueSnackbar(t(errorMessages), { variant: "error" });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    setIsLoading(true);
    handleClose();
    await logout();
    history.push("/login");
  };

  return (
    <>
      <PageLoading loading={isLoading} />
      <ToolbarBtn
        aria-controls="simple-menu"
        aria-haspopup="true"
        color="primary"
        data-testid="user-name"
        disableElevation
        onClick={handleClick}
        variant="contained"
      >
        {name || email}
      </ToolbarBtn>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        style={{ marginTop: 8 }}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
      >
        <MenuItem
          data-testid="header-menu__accounts-btn"
          onClick={() => setUserFormIsOpen(true)}
          classes={{ root: classes.menuItemRoot }}
          disabled={isDisableAccountSettings(user)}
        >
          <Typography display="block">
            {t("dashboard.accountSettings")}
          </Typography>
          {name && (
            <Tooltip title={user.email}>
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
                noWrap
                style={{ alignSelf: "flex-start", maxWidth: 200 }}
              >
                {user.email}
              </Typography>
            </Tooltip>
          )}
        </MenuItem>
        <MenuItem data-testid="header-menu__logout-btn" onClick={handleLogout}>
          <Typography>{t("dashboard.logOut")}</Typography>
        </MenuItem>
      </Menu>
      {userFormIsOpen && (
        <UserFormModal
          isOpen={userFormIsOpen}
          onClose={() => setUserFormIsOpen(false)}
          onSave={handleSave}
          user={user}
        />
      )}
    </>
  );
}

const isDisableAccountSettings = (user: AuthenticatedUser) => {
  const { organizationId, originalOrganizationId, superUser } = user;
  return superUser && organizationId !== originalOrganizationId;
};

const useStyles = makeStyles({
  menuItemRoot: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
});
